import { yupResolver } from '@hookform/resolvers/yup'
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  Switch,
  Typography
} from '@mui/material'
import { DataGridPro, GridOverlay } from '@mui/x-data-grid-pro'
import dayjs, { Dayjs } from 'dayjs'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import { ServiceItem } from '../../../hooks/useTransformServiceItems/useTransformServiceItems'
import {
  AuthorizationEntry,
  AuthorizationInfoViaEnum,
  CustomerEntry,
  ServiceEntry,
  ServiceInfoStatusEnum
} from '../../../sdk'
import theme from '../../../styles/theme'
import { formatCurrency } from '../../../utils'
import { formatName } from '../../../utils/formatCustomerName/formatCustomerName'
import { ControlledMuiSelect, ControlledMuiTextField } from '../../FormField'
import { DatePickerWithAlert } from '../../SharedUI/DatePickerWithAlert'
import { VALIDATE_AUTHORIZATION } from '../validation'

interface Props {
  handleCancel: () => void
  previousTotal?: number
  authorizations?: AuthorizationEntry[]
  newTotal: number
  customer: CustomerEntry
  handleCreateAuth: (data: FormInputs & { date: Date }) => void
  handleDeferService: (service: ServiceEntry) => void
  handleRecommendService: (service: ServiceEntry) => void
  serviceItems: ServiceItem[]
}

export interface FormInputs {
  via: AuthorizationInfoViaEnum
  authorizerName: string
  contactInfo: string
  notes: string
  viaDescription: string
}

const AuthorizationDialog: FC<Props> = ({
  handleCancel,
  previousTotal,
  authorizations,
  newTotal,
  customer,
  handleCreateAuth,
  handleDeferService,
  handleRecommendService,
  serviceItems
}) => {
  const customerName = useMemo(() => {
    if (customer) {
      return formatName(customer.information)
    }
    return 'N/A'
  }, [customer])

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { isSubmitting }
  } = useForm<FormInputs>({
    mode: 'onBlur',
    defaultValues: {
      authorizerName: formatName(customer.information),
      contactInfo: customer.information!.email || '',
      via: AuthorizationInfoViaEnum.Email,
      notes: '',
      viaDescription: ''
    },
    resolver: yupResolver(VALIDATE_AUTHORIZATION)
  })
  const [date, setDate] = useState<Dayjs | null>(dayjs(new Date()))
  const viaInput = watch('via')

  const submitHandler: SubmitHandler<FormInputs> = useCallback(
    async (data) => {
      if (!date) {
        return
      }

      handleCreateAuth({
        ...data,
        date: date.toDate()
      })
    },
    [date, handleCreateAuth]
  )

  useEffect(() => {
    if (viaInput === AuthorizationInfoViaEnum.Email) {
      setValue('authorizerName', customerName)
      setValue('contactInfo', customer.information!.email?.trim() || '')
    } else if (viaInput === AuthorizationInfoViaEnum.Phone) {
      setValue('authorizerName', customerName)
      setValue('contactInfo', customer.information!.phone?.trim() || '')
    } else if (viaInput === AuthorizationInfoViaEnum.InPerson) {
      setValue('authorizerName', customerName)
      setValue('contactInfo', '')
    }
  }, [customer.information, customerName, setValue, viaInput])
  const [showHistory, setShowHistory] = useState(false)

  return (
    <Dialog open={true} fullWidth={true} maxWidth="sm">
      <DialogTitle sx={{ typography: 'h3' }}>Authorize</DialogTitle>
      <DialogContent>
        <List dense={true}>
          <Divider />
          {serviceItems.filter(
            (service) => service.status === ServiceInfoStatusEnum.Authorized
          ).length > 0 && (
            <ListItem divider>
              <Typography variant="subtitle1">
                Previous Authorization
              </Typography>
            </ListItem>
          )}
          {serviceItems
            .filter(
              (service) => service.status === ServiceInfoStatusEnum.Authorized
            )
            .map((service) => (
              <ListItem divider key={service.id!} disablePadding>
                <ListItemIcon sx={{ justifyContent: 'center' }}>
                  <Checkbox
                    edge="start"
                    checked={true}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText sx={{ pr: '20px' }}>{service.name!}</ListItemText>
                <ListItemSecondaryAction>
                  <Typography>
                    {formatCurrency(service.cachedRetailPrice!, true)}
                  </Typography>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          {serviceItems.filter(
            (service) => service.status === ServiceInfoStatusEnum.Recommended
          ).length > 0 && (
            <ListItem divider>
              <Typography variant="subtitle1">Recommended</Typography>
            </ListItem>
          )}
          {serviceItems
            .filter(
              (service) => service.status === ServiceInfoStatusEnum.Recommended
            )
            .map((service) => (
              <ListItem divider key={service.id!} disablePadding>
                <ListItemIcon sx={{ justifyContent: 'center' }}>
                  <Checkbox
                    edge="start"
                    defaultChecked={true}
                    tabIndex={-1}
                    disableRipple
                    onChange={handleDeferService.bind(null, service.entry!)}
                  />
                </ListItemIcon>
                <ListItemText sx={{ pr: '20px' }}>{service.name!}</ListItemText>
                <ListItemSecondaryAction>
                  <Typography>
                    {formatCurrency(service.cachedRetailPrice!, true)}
                  </Typography>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          {serviceItems.filter(
            (service) => service.status === ServiceInfoStatusEnum.Deferred
          ).length > 0 && (
            <ListItem divider>
              <Typography variant="subtitle1">Deferred</Typography>
            </ListItem>
          )}
          {serviceItems
            .filter(
              (service) => service.status === ServiceInfoStatusEnum.Deferred
            )
            .map((service) => (
              <ListItem divider key={service.id!} disablePadding>
                <ListItemIcon sx={{ justifyContent: 'center' }}>
                  <Checkbox
                    edge="start"
                    defaultChecked={false}
                    tabIndex={-1}
                    disableRipple
                    onChange={handleRecommendService.bind(null, service.entry!)}
                  />
                </ListItemIcon>
                <ListItemText sx={{ pr: '20px' }}>{service.name!}</ListItemText>
                <ListItemSecondaryAction>
                  <Typography color={theme.palette.grey[500]}>
                    {formatCurrency(service.cachedRetailPrice!, true)}
                  </Typography>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          <ListItem
            divider
            secondaryAction={
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography>
                  Previous: {formatCurrency(previousTotal || 0, true)}
                </Typography>
                <Typography variant="subtitle1">
                  Total: {formatCurrency(newTotal, true)}
                </Typography>
              </Stack>
            }>
            <Typography variant="subtitle1">&nbsp;</Typography>
          </ListItem>
        </List>

        <form onSubmit={handleSubmit(submitHandler)} id="createAuthForm">
          <Grid container spacing={2} mt={2}>
            <Grid item xs={6}>
              <ControlledMuiTextField
                name="authorizerName"
                label="Name"
                control={control}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <DatePickerWithAlert
                label={'Authorization Date'}
                alertCondition={!date}
                alertText={'No date'}
                value={date}
                onChange={(newDate) => {
                  setDate(newDate)
                }}
                canClear={false}
                withTime={true}
                sx={{ width: '100%' }}
              />
            </Grid>
            <Grid item xs={6}>
              <ControlledMuiSelect
                fullWidth
                name="via"
                control={control}
                options={Object.keys(AuthorizationInfoViaEnum)
                  .filter(
                    (via) =>
                      AuthorizationInfoViaEnum[
                        via as keyof typeof AuthorizationInfoViaEnum
                      ] !== AuthorizationInfoViaEnum.CustomerPortal &&
                      AuthorizationInfoViaEnum[
                        via as keyof typeof AuthorizationInfoViaEnum
                      ] !== AuthorizationInfoViaEnum.CustomerIntake
                  )
                  .map((medium) => ({
                    id: AuthorizationInfoViaEnum[
                      medium as keyof typeof AuthorizationInfoViaEnum
                    ],
                    description: medium
                  }))}
                label="Method"
              />
            </Grid>
            <Grid item xs={6}>
              {viaInput !== AuthorizationInfoViaEnum.InPerson &&
                viaInput !== AuthorizationInfoViaEnum.Other && (
                  <ControlledMuiTextField
                    fullWidth
                    name="contactInfo"
                    control={control}
                    label={
                      viaInput === AuthorizationInfoViaEnum.Email
                        ? 'Email'
                        : 'Phone'
                    }
                    type={
                      viaInput === AuthorizationInfoViaEnum.Email
                        ? 'email'
                        : 'tel'
                    }
                  />
                )}
            </Grid>
            {viaInput === AuthorizationInfoViaEnum.Other && (
              <Grid item xs={12}>
                <ControlledMuiTextField
                  fullWidth
                  name="viaDescription"
                  control={control}
                  label="Method Description"
                  required
                  multiline
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <ControlledMuiTextField
                fullWidth
                name="notes"
                control={control}
                label="Reason"
                multiline
              />
            </Grid>
          </Grid>
        </form>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={showHistory}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setShowHistory(event.target.checked)
                }}
              />
            }
            label="Show History"
          />
        </FormGroup>
        <Collapse in={showHistory}>
          <DataGridPro
            hideFooter
            slots={{
              noRowsOverlay: () => (
                <GridOverlay>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      mt: 1
                    }}>
                    No Rows
                  </Box>
                </GridOverlay>
              )
            }}
            sx={{ minHeight: '120px' }}
            rows={
              authorizations
                ? authorizations?.map((authorization) => ({
                    id: authorization.id,
                    date: authorization.info!.dateTime,
                    method: authorization.info!.via,
                    amount: authorization.info!.amount
                  }))
                : []
            }
            columns={[
              {
                field: 'date',
                headerName: 'Date',
                valueFormatter: (params) =>
                  dayjs(params.value as string).format('MMM D, YYYY h:mm A'),
                flex: 1
              },
              {
                field: 'method',
                headerName: 'Method',
                flex: 1
              },
              {
                field: 'amount',
                headerName: 'Authorized Amount',
                valueFormatter: (params) =>
                  formatCurrency(params.value as number, true),
                flex: 1
              }
            ]}
          />
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button
          form="createAuthForm"
          type="submit"
          variant="contained"
          disabled={isSubmitting || !date}>
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AuthorizationDialog
